const ACQUISITION_SESSION_KEY = "websiteAcquisition";

const SEARCH_HOSTS = [
  "google.",
  "bing.com",
  "duckduckgo.com",
  "search.yahoo.com",
  "ecosia.org",
  "baidu.com",
  "yandex.",
];

const SOCIAL_HOSTS = [
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "t.co",
  "twitter.com",
  "x.com",
  "youtube.com",
  "reddit.com",
  "threads.net",
  "tiktok.com",
];

const PRIVATE_PATH_PREFIXES = ["/dashboard", "/login", "/postanarticle"];

const safelyParseUrl = (value, base) => {
  try {
    return new URL(value, base);
  } catch (error) {
    return null;
  }
};

const getExternalReferrer = () => {
  if (typeof document === "undefined" || !document.referrer) {
    return "";
  }

  const referrerUrl = safelyParseUrl(document.referrer);
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";

  if (!referrerUrl || (currentOrigin && referrerUrl.origin === currentOrigin)) {
    return "";
  }

  return referrerUrl.href;
};

const getClickProvider = (params) => {
  if (params.has("gclid")) return "Google Ads";
  if (params.has("msclkid")) return "Microsoft Ads";
  if (params.has("fbclid")) return "Meta Ads";
  if (params.has("ttclid")) return "TikTok Ads";
  return "";
};

export const classifyAcquisitionChannel = ({
  source = "",
  medium = "",
  referrerHost = "",
  clickProvider = "",
} = {}) => {
  const normalizedSource = source.toLowerCase();
  const normalizedMedium = medium.toLowerCase();
  const normalizedHost = referrerHost.toLowerCase();

  if (clickProvider || /cpc|ppc|paid|display|retarget/.test(normalizedMedium)) {
    return normalizedMedium.includes("social") || clickProvider === "Meta Ads"
      ? "Paid Social"
      : "Paid Search";
  }

  if (/email|newsletter/.test(normalizedMedium)) return "Email";
  if (/affiliate/.test(normalizedMedium)) return "Affiliate";
  if (/social/.test(normalizedMedium)) return "Organic Social";
  if (SEARCH_HOSTS.some((host) => normalizedHost.includes(host))) {
    return "Organic Search";
  }
  if (
    SOCIAL_HOSTS.some(
      (host) => normalizedHost.includes(host) || normalizedSource.includes(host),
    )
  ) {
    return "Organic Social";
  }
  if (normalizedHost || normalizedMedium === "referral") return "Referral";
  return "Direct";
};

const readStoredAcquisition = () => {
  if (typeof window === "undefined") return null;

  try {
    return JSON.parse(window.sessionStorage.getItem(ACQUISITION_SESSION_KEY));
  } catch (error) {
    return null;
  }
};

const storeAcquisition = (acquisition) => {
  try {
    window.sessionStorage.setItem(
      ACQUISITION_SESSION_KEY,
      JSON.stringify(acquisition),
    );
  } catch (error) {
    // Tracking must remain non-blocking when storage is unavailable.
  }
};

export const getAcquisitionSnapshot = (pageUrl = "/") => {
  if (typeof window === "undefined") return null;

  const currentUrl = safelyParseUrl(window.location.href, window.location.origin);
  const params = currentUrl?.searchParams || new URLSearchParams();
  const hasCampaignSignal = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "gclid",
    "msclkid",
    "fbclid",
    "ttclid",
  ].some((name) => params.has(name));
  const storedAcquisition = readStoredAcquisition();

  if (storedAcquisition && !hasCampaignSignal) {
    return storedAcquisition;
  }

  const referrer = getExternalReferrer();
  const referrerUrl = safelyParseUrl(referrer);
  const referrerHost = referrerUrl?.hostname.replace(/^www\./i, "") || "";
  const clickProvider = getClickProvider(params);
  const source =
    params.get("utm_source")?.trim() || clickProvider || referrerHost || "Direct";
  const medium =
    params.get("utm_medium")?.trim() ||
    (clickProvider ? "paid" : referrerHost ? "referral" : "none");
  const navigationEntry = window.performance
    ?.getEntriesByType?.("navigation")
    ?.find(Boolean);
  const acquisition = {
    source,
    medium,
    channel: classifyAcquisitionChannel({
      source,
      medium,
      referrerHost,
      clickProvider,
    }),
    campaign: params.get("utm_campaign")?.trim() || "",
    term: params.get("utm_term")?.trim() || "",
    content: params.get("utm_content")?.trim() || "",
    referrer,
    referrerHost,
    landingPage: pageUrl,
    clickProvider,
    redirectCount: Number(navigationEntry?.redirectCount) || 0,
    capturedAt: new Date().toISOString(),
  };

  storeAcquisition(acquisition);
  return acquisition;
};

export const shouldTrackPath = (pathname = "/") =>
  !PRIVATE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
